import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { credentials } from "../../../../helpers/api/credentials";
import { ShowToast } from "../../../../components/toast";
import type { CredentialKind, UpsertCredentialBody } from "../types";

/** Namespaced `["creds", …]` — a customer-scoped read, deliberately outside
 *  the `["th", …]` tree so a Tracking Health invalidation does not refetch it. */
export const credKeys = {
  all: ["creds"] as const,
  forUser: (userId: string) => ["creds", userId] as const,
};

/**
 * The hotel's resolved logins.
 *
 * `enabled` on a truthy userId is load-bearing rather than defensive: an
 * unclaimed Ads account has no matched owner, and firing this with an empty
 * id would put a guaranteed 404 on the page for every one of them.
 */
export function useHotelCredentials(userId: string | null | undefined) {
  return useQuery({
    queryKey: credKeys.forUser(userId ?? ""),
    queryFn: () => credentials.forUser(userId as string),
    enabled: Boolean(userId),
    staleTime: 60_000,
    refetchOnWindowFocus: false,
    // Credentials are a convenience on a diagnostic page — one retry, then
    // let the panel show its error rather than hammering on a 403.
    retry: 1,
  });
}

export function useUpsertCredential(userId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      kind,
      body,
    }: {
      kind: CredentialKind;
      body: UpsertCredentialBody;
    }) => credentials.upsert(userId, kind, body),
    // The write returns the re-resolved payload, so seed the cache with it
    // instead of round-tripping again.
    onSuccess: (data) => {
      qc.setQueryData(credKeys.forUser(userId), data);
      ShowToast({ message: "Credentials saved", type: "success" });
    },
    onError: (err: { response?: { data?: { message?: string } } }) => {
      ShowToast({
        message: err?.response?.data?.message ?? "Could not save credentials",
        type: "error",
      });
    },
  });
}

export function useRemoveCredential(userId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (kind: CredentialKind) => credentials.remove(userId, kind),
    onSuccess: (data) => {
      qc.setQueryData(credKeys.forUser(userId), data);
      ShowToast({
        message: "Override removed — the platform default applies again",
        type: "success",
      });
    },
    onError: () => {
      ShowToast({ message: "Could not remove the override", type: "error" });
    },
  });
}
